// src/DemoRequestedPage.js
// Thank you page shown after demo request - auto-redirects to comturk.com

import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Fade,
    Zoom,
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Email as EmailIcon,
    Schedule as ScheduleIcon,
} from '@mui/icons-material';

function DemoRequestedPage() {
    const [countdown, setCountdown] = useState(7);
    const [redirecting, setRedirecting] = useState(false);

    useEffect(() => {
        // Countdown timer
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    setRedirecting(true);
                    // Redirect to comturk.com
                    window.location.href = 'https://comturk.com';
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
                px: 2,
            }}
        >
            <Fade in timeout={800}>
                <Paper
                    elevation={10}
                    sx={{
                        p: { xs: 4, md: 6 },
                        maxWidth: 600,
                        width: '100%',
                        textAlign: 'center',
                        borderRadius: 4,
                        background: 'rgba(255, 255, 255, 0.98)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    }}
                >
                    {/* Success Icon */}
                    <Zoom in timeout={600}>
                        <Box
                            sx={{
                                width: 100,
                                height: 100,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 24px',
                                boxShadow: '0 10px 30px rgba(76, 175, 80, 0.4)',
                            }}
                        >
                            <CheckCircleIcon sx={{ fontSize: 60, color: '#fff' }} />
                        </Box>
                    </Zoom>

                    {/* Main Title */}
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 700,
                            color: '#1a1a2e',
                            mb: 2,
                            fontSize: { xs: '1.8rem', md: '2.5rem' },
                        }}
                    >
                        Thank You!
                    </Typography>

                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 500,
                            color: '#4F46E5',
                            mb: 4,
                            fontSize: { xs: '1.2rem', md: '1.5rem' },
                        }}
                    >
                        Your Demo Request Has Been Received
                    </Typography>

                    {/* Info Cards */}
                    <Box sx={{ mb: 4 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                                mb: 2,
                                p: 2,
                                bgcolor: '#f0f4ff',
                                borderRadius: 2,
                            }}
                        >
                            <ScheduleIcon sx={{ fontSize: 32, color: '#4F46E5' }} />
                            <Typography variant="body1" sx={{ color: '#333', fontWeight: 500 }}>
                                Your personalized demo will be ready in <strong>10-15 minutes</strong>
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                                p: 2,
                                bgcolor: '#f0fff4',
                                borderRadius: 2,
                            }}
                        >
                            <EmailIcon sx={{ fontSize: 32, color: '#22c55e' }} />
                            <Typography variant="body1" sx={{ color: '#333', fontWeight: 500 }}>
                                The demo link will be <strong>emailed to you</strong>
                            </Typography>
                        </Box>
                    </Box>

                    {/* What to Expect */}
                    <Box
                        sx={{
                            textAlign: 'left',
                            bgcolor: '#fafafa',
                            borderRadius: 2,
                            p: 3,
                            mb: 4,
                            border: '1px solid #eee',
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                            What Happens Next?
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5, color: '#555' }}>
                            <li style={{ marginBottom: '8px' }}>
                                Our AI is crawling your website to learn about your business
                            </li>
                            <li style={{ marginBottom: '8px' }}>
                                A personalized AI chatbot demo will be created just for you
                            </li>
                            <li style={{ marginBottom: '8px' }}>
                                You'll receive an email with your unique demo link
                            </li>
                            <li>
                                Try it out and see how AI can transform your customer support!
                            </li>
                        </Box>
                    </Box>

                    {/* Redirect Notice */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2,
                            p: 2,
                            bgcolor: 'rgba(79, 70, 229, 0.1)',
                            borderRadius: 2,
                            border: '1px solid rgba(79, 70, 229, 0.2)',
                        }}
                    >
                        {redirecting ? (
                            <>
                                <CircularProgress size={24} sx={{ color: '#4F46E5' }} />
                                <Typography variant="body2" sx={{ color: '#4F46E5', fontWeight: 500 }}>
                                    Redirecting to ComturkAI...
                                </Typography>
                            </>
                        ) : (
                            <Typography variant="body2" sx={{ color: '#4F46E5', fontWeight: 500 }}>
                                Redirecting to ComturkAI in <strong>{countdown}</strong> seconds...
                            </Typography>
                        )}
                    </Box>

                    {/* Logo / Branding */}
                    <Box sx={{ mt: 4 }}>
                        <Typography variant="body2" sx={{ color: '#999' }}>
                            Powered by
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            ComturkAI
                        </Typography>
                    </Box>
                </Paper>
            </Fade>
        </Box>
    );
}

export default DemoRequestedPage;
